import { JsonConverter, JsonObject, JsonProperty } from "ta-json";

import {
    ConditionalValidationResource,
    ConditionalValidationResourceConverter,
} from "../../models/conditional-validation-resource";
import { PropertyDataType } from "./data-type";
import { IMemberDefinition, MemberDefinition } from "./member-definition";
import { MemberDefinitionType } from "./member-definition-type";

/**
 * Represents a data schema / domain model for properties on an {@link IEntityDefinition}.
 */
export interface IPropertyDefinition extends IMemberDefinition {
    /**
     * The {@link DataType} of the value for this property.
     */
    readonly dataType: PropertyDataType;

    /**
     * Indicates whether the property value is indexed and thus searchable via the basic querying API.
     * Search for value with indexed querying will always work.
     * Note that this value should be set only if required and with good understanding of potential consequences.
     */
    indexed: boolean;

    /**
     * Indicates whether the property is required to be set.
     */
    isMandatory: boolean;

    /**
     * Indicates whether the property is culture sensitive or not.
     */
    isMultiLanguage: boolean;

    /**
     * Indicates whether the value is an array.
     */
    isMultiValue: boolean;

    /**
     * Indicates whether the value of the property is unique among all the property values of this property definition
     * and entity definition.
     */
    isUnique: boolean;

    /**
     * Indicates whether the content of this property is boosted when searched for via index querying (Elasticsearch).
     */
    boost: boolean;

    /**
     * Indicates whether the property value is included in the full text search content of the Elasticsearch document.
     */
    includeInContent: boolean;

    /**
     * Indicates whether the property value is included in the collection of words used for auto-complete functionality.
     */
    includeInCompletion: boolean;

    /**
     * Indicates whether this member definition should be stored.
     */
    storedInGraph: boolean;

    /**
     * Indicates whether this member definition will be ignored during imports.
     */
    ignoreOnExport: boolean;

    /**
     * An optional set of conditions that dictate under what conditions the current property is required.
     */
    conditionalValidation?: ConditionalValidationResource;
}

@JsonObject()
export class PropertyDefinition extends MemberDefinition implements IPropertyDefinition {
    @JsonProperty("is_indexed")
    indexed: boolean;

    @JsonProperty("is_mandatory")
    isMandatory: boolean;

    @JsonProperty("is_multilanguage")
    isMultiLanguage: boolean;

    @JsonProperty("is_multivalue")
    isMultiValue: boolean;

    @JsonProperty("is_unique")
    isUnique: boolean;

    @JsonProperty("boost")
    boost: boolean;

    @JsonProperty("included_in_content")
    includeInContent: boolean;

    @JsonProperty("included_in_completion")
    includeInCompletion: boolean;

    @JsonProperty("ignore_on_export")
    ignoreOnExport: boolean;

    @JsonProperty("stored_in_graph")
    storedInGraph: boolean;

    @JsonProperty("conditional_validation")
    @JsonConverter(ConditionalValidationResourceConverter)
    conditionalValidation?: ConditionalValidationResource;

    get dataType(): PropertyDataType {
        return this.type as PropertyDataType;
    }

    get definitionType(): MemberDefinitionType {
        return MemberDefinitionType.Property;
    }

    constructor(name: string, type: PropertyDataType) {
        super(name, type);
    }
}
